import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { AgGridReact } from 'ag-grid-react';
import { Link, Navigate } from 'react-router-dom';
import * as Bixby from '$/core';
import BixButton from '$/components/global/bixButton';
import { CompanyService } from '$/services/CompanyService';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { getIsCurrentUserDataAdminState } from '$/store/profile/selectors';
import dayjs from 'dayjs';
import './index.scss';

interface IIssuerLevelMappingProps {
  isDataAdmin: boolean;
  industries?: Bixby.Models.IIdentifiable[];
  industryGroups?: Bixby.Models.IIdentifiable[];
  sectors?: Bixby.Models.IIdentifiable[];
  fitchIndutries?: Bixby.Models.IIdentifiable[];
  dispatchToastError: (message: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
}

const IssuerLevelMapping: React.FC<IIssuerLevelMappingProps> = props => {
  const { isDataAdmin, industries, industryGroups, sectors, fitchIndutries, dispatchToastError, dispatchToastSuccess } =
    props;
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [mappingList, setMappingList] = useState<Bixby.Models.CompanyIssuerLevelMapping[] | null>(null);
  let mappedData: Bixby.Models.CompanyIssuerLevelMappingUpdate[] = [];

  useEffect(() => {
    const getCompanyIssuerLevelMappingAsync = async () => {
      if (!isUpdating) {
        try {
          setIsLoading(true);
          setErrorMessage(undefined);
          //call service to get company Issuer Level Mapping data
          const results = await CompanyService.getCompanyIssuerLevelMappingAsync();
          setMappingList(results);
        } catch (e: any) {
          setErrorMessage('Error retrieving company Issuer Level Mapping data');
        } finally {
          setIsLoading(false);
        }
      }
    };

    getCompanyIssuerLevelMappingAsync();
  }, [isUpdating]);

  function handleSaveCreditSightsMapping() {
    updateCreditSightsMapping();
  }

  const updateCreditSightsMapping = async () => {
    try {
      setIsUpdating(true);
      if (mappedData.length > 0) {
        await CompanyService.updateCompanyIssuerLevelMappingAsync(mappedData);
        dispatchToastSuccess('Mapping Updated Successfully');
      } else {
        dispatchToastError('No Rows Updated');
      }
    } catch {
      dispatchToastError('Failed to update mapping');
    } finally {
      setIsUpdating(false);
      mappedData.splice(0, mappedData.length);
    }
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const linkCellFormatter = (params: any) => {
    const url = `/admin/company/` + params.data['companyId'];
    return (
      <Link className="link_underline bix-link" to={url}>
        {' '}
        {` ${params.value} `}{' '}
      </Link>
    );
  };

  const handleCellValueChange = (params: any) => {
    const companyId = params.data['companyId'];
    // if already exist in object array to update
    if (mappedData.some(_ => _.companyId === companyId)) {
      const data: Bixby.Models.CompanyIssuerLevelMappingUpdate = {
        creditSightsCompanyId: params.data['creditSightsCompanyId'],
        industryId: params.data['industryId'],
        industryGroupId: params.data['industryGroupId'],
        fitchIndustryId: params.data['fitchIndustryId'],
        sectorId: params.data['sectorId'],
        fiscalYearEnd: params.data['fiscalYearEnd'],
        companyId: params.data['companyId'],
      };

      data[params.colDef.field] = params.newValue;
      mappedData = mappedData.map(item => (item.companyId === companyId ? data : item));
    }
    // if not exist in object array to update, add new
    else {
      const data: Bixby.Models.CompanyIssuerLevelMappingUpdate = {
        creditSightsCompanyId: params.data['creditSightsCompanyId'],
        industryId: params.data['industryId'],
        industryGroupId: params.data['industryGroupId'],
        fitchIndustryId: params.data['fitchIndustryId'],
        sectorId: params.data['sectorId'],
        fiscalYearEnd: params.data['fiscalYearEnd'],
        companyId: params.data['companyId'],
      };
      data[params.colDef.field] = params.newValue;
      mappedData.push(data);
    }
  };

  const monthOptions: Bixby.Models.IIdentifiable[] = dayjs.monthsShort().map(x => {
    return {
      id: x,
      name: x,
    };
  });

  if (!isDataAdmin) {
    return <Navigate to="/admin" />;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <React.Fragment>
        <div className="creditsights_mapping_header">
          <HeadingBlock title={'Issuer Level Mapping'} />
          <div className="update_creditsights_mapping">
            <BixButton isLoading={isUpdating} disabled={isUpdating} onClick={handleSaveCreditSightsMapping}>
              Save
            </BixButton>
          </div>
        </div>
        {mappingList && !Bixby.Utils.isNullOrEmpty(mappingList) ? (
          <section className="creditsights_mapping_list">
            <div className="creditsights_mapping_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={true}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                    }}
                    rowData={mappingList}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    onCellValueChanged={handleCellValueChange}
                    animateRows={true}
                    rowHeight={32}
                    stopEditingWhenCellsLoseFocus={true}
                    domLayout="autoHeight"
                    headerHeight={32}
                    columnDefs={[
                      {
                        field: 'companyName',
                        headerName: 'Company Name',
                        width: 200,
                        floatingFilter: true,
                        cellRenderer: linkCellFormatter,
                      },
                      {
                        field: 'bixbyId',
                        headerName: 'Bixby Id',
                        width: 200,
                        cellClass: 'align-left',
                      },
                      { field: 'openFigiTicker', headerName: 'Open FIGI Ticker', sort: 'asc', width: 100 },
                      {
                        field: 'creditSightsCompanyId',
                        headerName: 'CreditSights Company ID',
                        width: 100,
                        editable: true,
                        cellDataType: 'number',
                        valueParser: params => {
                          const value = parseInt(params.newValue, 10);
                          return isNaN(value) || value > 2147483647 || value < 0 ? params.oldValue : value;
                        },
                      },
                      {
                        field: 'fiscalYearEnd',
                        headerName: 'Fiscal Year End',
                        width: 100,
                        editable: true,
                        cellEditor: 'agSelectCellEditor',
                        cellEditorParams: {
                          values: monthOptions?.map(_ => _.id),
                        },
                        suppressKeyboardEvent: params => {
                          return params.event.key === 'Delete' || params.event.key === 'Backspace';
                        },
                      },
                      {
                        field: 'industryId',
                        headerName: 'Industry',
                        width: 100,
                        editable: true,
                        cellEditor: 'agSelectCellEditor',
                        cellEditorParams: {
                          values: industries?.map(_ => _.id),
                        },
                        valueFormatter: params => {
                          const match = industries?.find(_ => _.id === params.value);
                          return match ? match.name : '';
                        },
                        valueParser: params => {
                          const newValue = params.newValue.trim();
                          const match = industries?.find(_ => _.name === newValue);
                          if (match) return match.id;
                          const isGuid = industries?.some(_ => _.id === newValue);
                          return isGuid ? newValue : params.oldValue;
                        },
                        suppressKeyboardEvent: params => {
                          return params.event.key === 'Delete' || params.event.key === 'Backspace';
                        },
                      },
                      {
                        field: 'industryGroupId',
                        headerName: 'Industry Group',
                        width: 100,
                        editable: true,
                        cellEditor: 'agSelectCellEditor',
                        cellEditorParams: {
                          values: industryGroups?.map(_ => _.id),
                        },
                        valueFormatter: params => {
                          const match = industryGroups?.find(_ => _.id === params.value);
                          return match ? match.name : '';
                        },
                        valueParser: params => {
                          const newValue = params.newValue.trim();
                          const match = industryGroups?.find(_ => _.name === newValue);
                          if (match) return match.id;
                          const isGuid = industryGroups?.some(_ => _.id === newValue);
                          return isGuid ? newValue : params.oldValue;
                        },
                      },
                      {
                        field: 'fitchIndustryId',
                        headerName: 'Fitch Industry',
                        width: 100,
                        editable: true,
                        cellEditor: 'agSelectCellEditor',
                        cellEditorParams: {
                          values: fitchIndutries?.map(_ => _.id),
                        },
                        valueFormatter: params => {
                          const match = fitchIndutries?.find(_ => _.id === params.value);
                          return match ? match.name : '';
                        },
                        valueParser: params => {
                          const newValue = params.newValue.trim();
                          const match = fitchIndutries?.find(_ => _.name === newValue);
                          if (match) return match.id;
                          const isGuid = fitchIndutries?.some(_ => _.id === newValue);
                          return isGuid ? newValue : params.oldValue;
                        },
                      },
                      {
                        field: 'sectorId',
                        headerName: 'Sector',
                        width: 100,
                        editable: true,
                        cellEditor: 'agSelectCellEditor',
                        cellEditorParams: {
                          values: sectors?.map(_ => _.id),
                        },
                        valueFormatter: params => {
                          const match = sectors?.find(_ => _.id === params.value);
                          return match ? match.name : '';
                        },
                        valueParser: params => {
                          const newValue = params.newValue.trim();
                          const match = sectors?.find(_ => _.name === newValue);
                          if (match) return match.id;
                          const isGuid = sectors?.some(_ => _.id === newValue);
                          return isGuid ? newValue : params.oldValue;
                        },
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <>
            {errorMessage && <div>{errorMessage}</div>}
            <div className="creditsights_mapping_no_data">
              <NoDataFound dataType="companies" />
            </div>
          </>
        )}
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isDataAdmin } = getIsCurrentUserDataAdminState(state);

  return {
    isDataAdmin: isDataAdmin,
    industries: state.industryState.data,
    industryGroups: state.industryGroupState.data,
    sectors: state.sectorState.data,
    fitchIndutries: state.fitchIndustryState.data,
  };
};
const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IssuerLevelMapping);
